import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { API, api, useAuth } from "@/lib/auth";
import { MARKET_INDICES, SECTORS, RADAR_CATEGORIES, RADAR_CITIES, MARKET_BRIEF } from "@/data/demo";
import { PageHeader, Card, Btn, Icon, DemoBadge, DataSource, AIDisclaimer } from "@/components/kit";
import { toast } from "sonner";

const BRIEF_TONE = { amber: "#F59E0B", blue: "#3B82F6", violet: "#8B5CF6", lime: "#84CC16" };

function heat(v) {
  if (v >= 1.2) return "#16A34A"; if (v > 0) return "#4ADE80";
  if (v <= -1) return "#DC2626"; if (v < 0) return "#F97316"; return "#9CA3AF";
}
function arrows(t) { return t >= 2 ? "↑↑" : t === 1 ? "↑" : t <= -1 ? "↓" : "→"; }

export default function Market() {
  const [explain, setExplain] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  const { refreshUser, user } = useAuth();
  const [reports, setReports] = useState([]);
  const [rcat, setRcat] = useState("All");
  const [rf, setRf] = useState({ title: "", source: "AMFI", description: "", month: "", category: "Industry", file: null, thumb: null });
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const ORIGIN = API.replace(/\/api$/, "");
  const resolveUrl = (u) => (u?.startsWith("http") ? u : ORIGIN + u);
  const loadReports = () => api.get("/market/reports").then((r) => setReports(r.data)).catch(() => {});
  useEffect(() => { loadReports(); }, []);
  const REPORT_CATS = ["Industry", "SEBI", "Markets"];
  const shownReports = reports.filter((r) => rcat === "All" || (r.category || "Industry") === rcat);
  const unwatched = reports.filter((r) => !r.watched).length;
  const captureFrame = (file) => new Promise((resolve) => {
    try {
      const url = URL.createObjectURL(file);
      const v = document.createElement("video");
      v.preload = "metadata"; v.muted = true; v.src = url;
      v.onloadeddata = () => { try { v.currentTime = Math.min(1, (v.duration || 2) / 2); } catch (e) { resolve(null); } };
      v.onseeked = () => {
        try {
          const c = document.createElement("canvas");
          c.width = v.videoWidth || 1280; c.height = v.videoHeight || 720;
          c.getContext("2d").drawImage(v, 0, 0, c.width, c.height);
          c.toBlob((b) => { URL.revokeObjectURL(url); resolve(b); }, "image/jpeg", 0.8);
        } catch (e) { URL.revokeObjectURL(url); resolve(null); }
      };
      v.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
    } catch (e) { resolve(null); }
  });
  const markWatched = async (id) => {
    setReports((rs) => rs.map((x) => (x.id === id ? { ...x, watched: true } : x)));
    try { await api.post(`/market/reports/${id}/watch`); } catch (e) { /* noop */ }
  };
  const uploadReport = async () => {
    if (!rf.title.trim() || !rf.file) { toast.error("Add a title and choose a video file."); return; }
    setUploading(true); setProgress(0);
    try {
      let thumb = rf.thumb;
      if (!thumb) { const b = await captureFrame(rf.file); if (b) thumb = new File([b], "poster.jpg", { type: "image/jpeg" }); }
      const fd = new FormData();
      fd.append("title", rf.title.trim()); fd.append("source", rf.source || "AMFI");
      fd.append("description", rf.description || ""); fd.append("month", rf.month || ""); fd.append("category", rf.category || "Industry");
      fd.append("file", rf.file);
      if (thumb) fd.append("thumbnail", thumb);
      await api.post("/market/reports/upload", fd, { onUploadProgress: (e) => { if (e.total) setProgress(Math.round((e.loaded * 100) / e.total)); } });
      toast.success("Report uploaded.");
      setRf({ title: "", source: "AMFI", description: "", month: "", category: "Industry", file: null, thumb: null });
      loadReports();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed."); }
    finally { setUploading(false); setProgress(0); }
  };
  const deleteReport = async (id) => {
    try { await api.delete(`/market/reports/${id}`); loadReports(); toast.success("Removed."); }
    catch { toast.error("Could not remove."); }
  };

  const run = async () => {
    setBusy(true); setExplain("");
    try {
      const doFetch = () => fetch(`${API}/ai/chat`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: "Explain today's market: Nifty +0.72%, IT and Banking led gains, midcaps slightly down. Structure it for a retail investor.", mode: "market" }),
      });
      let res = await doFetch();
      if (res.status === 401) { try { await api.post("/auth/refresh"); res = await doFetch(); } catch (e) { console.error("Auth refresh failed", e); } }
      if (res.status === 402) { toast.error("Insufficient wallet balance."); setBusy(false); return; }
      if (!res.ok) { toast.error("AI error"); setBusy(false); return; }
      const reader = res.body.getReader(); const dec = new TextDecoder(); let buf = "";
      while (true) { const { done, value } = await reader.read(); if (done) break;
        buf += dec.decode(value, { stream: true }); const parts = buf.split("\n\n"); buf = parts.pop();
        for (const p of parts) { if (!p.startsWith("data: ")) continue; const d = JSON.parse(p.slice(6)); if (d.delta) setExplain((s) => s + d.delta); } }
      refreshUser();
    } catch { toast.error("AI error"); } finally { setBusy(false); }
  };

  return (
    <div className="fade-up">
      <PageHeader title="Market Intelligence" subtitle="What matters to an MFD today — indices, sectors and practical client implications." icon="TrendingUp" color="#06B6D4" />

      {/* Industry Reports (video briefings) */}
      <Card className="mb-6" data-testid="market-reports">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div className="flex items-center gap-2"><Icon name="Video" size={20} style={{ color: "#06B6D4" }} /><h3 className="font-head font-semibold text-lg">MFDx Industry Reports</h3></div>
          <span className="text-xs text-gray-400">{unwatched > 0 ? `${unwatched} new to watch` : "Video briefings for distributors"}</span>
        </div>
        {reports.length > 0 && (
          <div className="flex gap-1.5 mb-4 flex-wrap" data-testid="market-report-filters">
            {["All", ...REPORT_CATS].map((c) => (
              <button key={c} onClick={() => setRcat(c)} data-testid={`report-filter-${c.toLowerCase()}`}
                className={`px-3 py-1 rounded-full text-xs font-semibold border ${rcat === c ? "bg-cyan-600 text-white border-transparent" : "bg-white text-gray-600 border-gray-200"}`}>{c}</button>
            ))}
          </div>
        )}
        {reports.length === 0 ? (
          <p className="text-sm text-gray-400">No reports yet.</p>
        ) : shownReports.length === 0 ? (
          <p className="text-sm text-gray-400">No reports in this category.</p>
        ) : (
          <div className="grid md:grid-cols-2 gap-5">
            {shownReports.map((r) => (
              <div key={r.id} className="rounded-2xl border border-gray-100 overflow-hidden" data-testid="market-report-card">
                <div className="relative">
                  <video src={resolveUrl(r.url)} poster={r.thumbnail ? resolveUrl(r.thumbnail) : undefined} onPlay={() => markWatched(r.id)} controls playsInline preload="metadata" className="w-full aspect-video bg-black" data-testid="market-report-video" />
                  {r.watched && <span className="absolute top-2 right-2 text-[10px] font-bold bg-emerald-500 text-white rounded-full px-2 py-0.5 inline-flex items-center gap-1" data-testid="market-report-watched"><Icon name="Check" size={10} /> Watched</span>}
                </div>
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <h4 className="font-semibold text-sm">{r.title}</h4>
                    {user?.role === "admin" && <button onClick={() => deleteReport(r.id)} data-testid="market-report-delete" className="text-gray-300 hover:text-red-500 shrink-0"><Icon name="Trash2" size={15} /></button>}
                  </div>
                  {r.description && <p className="text-xs text-gray-500 mt-1 leading-relaxed" data-testid="market-report-desc">{r.description}</p>}
                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                    <span className="text-[11px] font-semibold bg-violet-50 text-violet-700 rounded-full px-2 py-0.5" data-testid="market-report-category">{r.category}</span>
                    <span className="text-[11px] font-semibold bg-cyan-50 text-cyan-700 rounded-full px-2 py-0.5 inline-flex items-center gap-1"><Icon name="BadgeCheck" size={11} /> Source: {r.source}</span>
                    {r.month && <span className="text-[11px] font-semibold bg-gray-100 text-gray-600 rounded-full px-2 py-0.5" data-testid="market-report-month">{r.month}</span>}
                    {r.created_at && <span className="text-[11px] text-gray-400">{new Date(r.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        {user?.role === "admin" && (
          <div className="mt-5 rounded-2xl border border-dashed border-gray-200 bg-gray-50/60 p-4" data-testid="market-report-upload">
            <div className="flex items-center gap-2 mb-3"><Icon name="Upload" size={16} className="text-cyan-600" /><span className="font-semibold text-sm">Admin — upload a report</span></div>
            <div className="grid sm:grid-cols-2 gap-2">
              <input value={rf.title} onChange={(e) => setRf({ ...rf, title: e.target.value })} placeholder="Report title" data-testid="report-title-input" className="rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-cyan-400" />
              <input value={rf.month} onChange={(e) => setRf({ ...rf, month: e.target.value })} placeholder="Month tag (e.g. August 2026)" data-testid="report-month-input" className="rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-cyan-400" />
            </div>
            <textarea value={rf.description} onChange={(e) => setRf({ ...rf, description: e.target.value })} placeholder="Short summary (optional)" rows={2} data-testid="report-desc-input" className="mt-2 w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-cyan-400 resize-none" />
            <input value={rf.source} onChange={(e) => setRf({ ...rf, source: e.target.value })} placeholder="Source (e.g. AMFI)" data-testid="report-source-input" className="mt-2 w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-cyan-400" />
            <select value={rf.category} onChange={(e) => setRf({ ...rf, category: e.target.value })} data-testid="report-category-input" className="mt-2 w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-cyan-400 bg-white">
              {REPORT_CATS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <label className="block text-xs text-gray-500 mt-3 mb-1">Video file</label>
            <input type="file" accept="video/*" onChange={(e) => setRf({ ...rf, file: e.target.files?.[0] || null })} data-testid="report-file-input" className="block w-full text-sm text-gray-600 file:mr-3 file:rounded-lg file:border-0 file:bg-cyan-600 file:text-white file:px-3 file:py-2 file:text-sm file:font-semibold" />
            <label className="block text-xs text-gray-500 mt-3 mb-1">Poster image (optional)</label>
            <input type="file" accept="image/*" onChange={(e) => setRf({ ...rf, thumb: e.target.files?.[0] || null })} data-testid="report-thumb-input" className="block w-full text-sm text-gray-600 file:mr-3 file:rounded-lg file:border-0 file:bg-gray-200 file:text-gray-700 file:px-3 file:py-2 file:text-sm file:font-semibold" />
            {uploading && (
              <div className="mt-3" data-testid="report-upload-progress">
                <div className="h-2 w-full rounded-full bg-gray-200 overflow-hidden"><div className="h-full bg-cyan-500 transition-all duration-200" style={{ width: `${progress}%` }} /></div>
                <div className="text-[11px] text-gray-500 mt-1">{progress < 100 ? `Uploading… ${progress}%` : "Processing…"}</div>
              </div>
            )}
            <Btn onClick={uploadReport} disabled={uploading} className="mt-3" data-testid="report-upload-btn"><Icon name="Upload" size={15} /> {uploading ? "Uploading…" : "Upload report"}</Btn>
          </div>
        )}
      </Card>

      {/* What matters to an MFD today */}
      <div className="rounded-[24px] bg-[#0A0A0A] text-white p-6 md:p-7 mb-6 relative overflow-hidden" data-testid="mfd-brief">
        <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full grad-lime-cyan opacity-25 blur-3xl" />
        <div className="flex items-center gap-2 mb-1 relative"><Icon name="Sparkles" size={18} className="text-lime-300" /><span className="font-head font-semibold text-lg">What matters to an MFD today</span></div>
        <p className="text-gray-400 text-sm mb-5 relative">Market moves translated into what you should do and say to clients.</p>
        <div className="grid md:grid-cols-2 gap-3 relative">
          {MARKET_BRIEF.map((b) => (
            <div key={b.title} className="bg-white/5 border border-white/10 rounded-2xl p-4">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-7 h-7 rounded-lg grid place-items-center" style={{ background: `${BRIEF_TONE[b.tone]}22` }}><Icon name={b.icon} size={14} style={{ color: BRIEF_TONE[b.tone] }} /></div>
                <span className="font-semibold text-sm">{b.title}</span>
              </div>
              <p className="text-xs text-gray-400 leading-relaxed">{b.detail}</p>
              <div className="text-xs text-gray-200 mt-2 flex items-start gap-1.5"><Icon name="ArrowRight" size={13} className="mt-0.5 shrink-0 text-lime-300" />{b.implication}</div>
            </div>
          ))}
        </div>
        <div className="mt-5 relative">
          <Btn onClick={() => nav("/app/ai", { state: { prompt: "Using today's market (Nifty +0.72%, IT and Banking led, midcaps -0.34%, 10Y G-Sec 6.84%), write a short, reassuring WhatsApp message I can send to my clients that reinforces SIP discipline. Add a one-line risk disclaimer." } })} className="bg-white/15" data-testid="brief-draft-btn"><Icon name="MessageCircle" size={15} /> Draft a client update</Btn>
        </div>
      </div>

      {/* Ticker */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {MARKET_INDICES.map((m) => (
          <div key={m.name} className="card-mfx p-4">
            <div className="text-xs text-gray-400">{m.name}</div>
            <div className="font-head font-bold text-lg mt-1">{m.value}</div>
            <div className={`text-xs font-semibold ${m.up ? "text-emerald-600" : "text-red-500"}`}>{m.up ? "▲" : "▼"} {m.chg}</div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-5 mb-6">
        <Card>
          <div className="flex items-center justify-between mb-4"><h3 className="font-head font-semibold text-lg">Sector performance</h3><DemoBadge /></div>
          <div className="space-y-2">
            {SECTORS.map((s) => (
              <div key={s.name} className="flex items-center gap-3">
                <span className="w-20 text-sm">{s.name}</span>
                <div className="flex-1 h-6 bg-gray-50 rounded-lg relative overflow-hidden">
                  <div className="h-full rounded-lg absolute top-0" style={{ width: `${Math.min(Math.abs(s.chg) * 40, 100)}%`, background: heat(s.chg), left: s.chg < 0 ? "auto" : 0, right: s.chg < 0 ? 0 : "auto", opacity: 0.85 }} />
                </div>
                <span className={`w-14 text-right text-sm font-semibold ${s.chg >= 0 ? "text-emerald-600" : "text-red-500"}`}>{s.chg >= 0 ? "+" : ""}{s.chg}%</span>
              </div>
            ))}
          </div>
          <DataSource />
        </Card>

        {/* AI explain */}
        <div className="rounded-[20px] bg-[#0A0A0A] text-white p-6 flex flex-col">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2"><Icon name="Sparkles" size={18} className="text-blue-300" /><span className="font-head font-semibold">Explain today's market</span></div>
            <Btn onClick={run} disabled={busy} className="bg-white/15">{busy ? "…" : "Explain"}</Btn>
          </div>
          <div className="flex-1 overflow-y-auto text-sm text-gray-200 whitespace-pre-wrap leading-relaxed min-h-[180px]">
            {explain || <span className="text-gray-500">Tap "Explain" — MFDX AI converts market movement → reason → sectors → investor implication → a client-ready message.</span>}
          </div>
          <AIDisclaimer text="AI explanation of illustrative demo data. Not personalised investment advice." />
        </div>
      </div>

      {/* Market Radar */}
      <Card className="mb-6">
        <div className="flex items-center gap-2 mb-4"><Icon name="Radar" size={20} style={{ color: "#8B5CF6" }} /><h3 className="font-head font-semibold text-lg">Market Radar — category momentum</h3><DemoBadge /></div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {RADAR_CATEGORIES.map((c) => (
            <div key={c.cat} className="rounded-2xl p-4 text-center" style={{ background: `${heat(c.trend)}15` }}>
              <div className="text-sm font-medium">{c.cat}</div>
              <div className="font-head font-bold text-2xl mt-1" style={{ color: heat(c.trend) }}>{arrows(c.trend)}</div>
            </div>
          ))}
        </div>
        <DataSource src="Illustrative demo — not live scheme flow data" />
      </Card>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        {RADAR_CITIES.map((c) => (
          <Card key={c.city} className="hover-lift">
            <div className="flex items-center justify-between"><h4 className="font-head font-semibold">{c.city}</h4><span className="text-xs font-bold text-emerald-600">{c.growth}</span></div>
            <p className="text-sm text-gray-500 mt-2">{c.note}</p>
          </Card>
        ))}
      </div>
    </div>
  );
}
